/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useQueryCache } from 'react-query';
import { useTranslation } from 'react-i18next';

import Checkbox from 'components/form/checkbox/Checkbox';
import { Box, Flex } from 'components/styled';
import Paper from 'components/styled/Paper';
import Text from 'components/styled/Text';
import Button from 'components/styled/Button';
import Form from 'components/form';
import TextInput from 'components/form/input/TextInput';
import MultistringInput from 'components/form/input/MultistringInput';

import { Backend } from 'api/endpoints';
import { updateProduct } from 'api/productApi';

import { multistringSchema, shortStringSchema } from 'utils/validation';

type Props = {
	product: Backend.Product;
	disabled?: boolean;
	closeModal: () => void;
};

const ProductEdit: React.FC<Props> = ({ product, closeModal }) => {
	const { t } = useTranslation('components/tiles/product_tab/product_edit/');

	const cache = useQueryCache();

	const formikContext = useFormik({
		initialValues: {
			...product,

			offersCount: product.offersCount === -1 ? 0 : product.offersCount,
			offersUnlimited: product.offersCount === -1,

			revealsCount: product.revealsCount === -1 ? 0 : product.revealsCount,
			revealsUnlimited: product.revealsCount === -1,
		},
		onSubmit: async values => {
			if (values.offersUnlimited) {
				values.offersCount = -1;
			}
			if (values.revealsUnlimited) {
				values.revealsCount = -1;
			}

			try {
				await updateProduct(product.id, values);
				closeModal();
				await cache.invalidateQueries('products');
				toast.success(
					t('product_edited', {
						productName: product.name.CZECH,
					}),
				);
			} catch (error) {
				toast.error(
					t('product_error', {
						productName: product.name.CZECH,
					}),
				);
			}
		},
		validationSchema: Yup.object({
			name: multistringSchema(shortStringSchema, true),
			priceWithoutTax: Yup.number()
				.min(0, 'positive_number')
				.max(9999999)
				.required(),
			offersCount: Yup.number().when('offersUnlimited', {
				is: false,
				then: Yup.number().min(0, 'positive_number').max(9999999).required(),
			}),
			revealsCount: Yup.number().when('revealsUnlimited', {
				is: false,
				then: Yup.number().min(0, 'positive_number').max(9999999).required(),
			}),
		}),
	});
	const { productType: type } = product;

	const { setFieldValue, values, touched, isSubmitting } = formikContext;

	return (
		<Box>
			<Form formikContext={formikContext}>
				<Paper
					css={css`
						border-radius: 3px;
						box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
						border: 0;
					`}
				>
					<Flex alignItems="center" flexDirection="column">
						<Text fontSize="xl" fontWeight="bold" mb={3}>
							{product.name.CZECH}
						</Text>
					</Flex>

					<Box p={3}>
						<MultistringInput
							name="name"
							label={t('package_name')}
							placeholder={t('package_name_placeholder')}
						/>
					</Box>

					{(type === 'LICENSE' || type === 'OFFERS') && (
						<Flex
							alignItems={['flex-start', 'flex-end']}
							flexDirection="column"
							p={3}
						>
							<TextInput
								type="number"
								name="offersCount"
								label={t('number_offers')}
								placeholder={values.offersUnlimited ? '∞' : t('number_offers')}
								// TODO: value={values.offersUnlimited ? '' : values.offersCount}
								iconRight={
									<Box width={30} textAlign="right">
										{t('qty')}
									</Box>
								}
								disabled={values.offersUnlimited}
								css={css`
									text-align: right;
								`}
							/>
							<Checkbox
								id="offersUnlimited"
								name="offersUnlimited"
								label={t('unlimited')}
								checked={values.offersUnlimited}
								onSetValue={setFieldValue}
								disabled={isSubmitting}
								touched={touched.offersUnlimited}
							/>
							<Box></Box>
						</Flex>
					)}

					{(type === 'LICENSE' || type === 'REVEALS') && (
						<Flex
							alignItems={['flex-start', 'flex-end']}
							flexDirection="column"
							p={3}
						>
							<TextInput
								type="number"
								name="revealsCount"
								label={t('reveals_count')}
								placeholder={values.revealsUnlimited ? '∞' : t('reveals_count')}
								// TODO: value={values.revealsUnlimited ? '' : values.revealsCount}
								iconRight={
									<Box width={30} textAlign="right">
										{t('qty')}
									</Box>
								}
								disabled={values.revealsUnlimited}
								// TODO: maxLength={9}
								css={css`
									text-align: right;
								`}
							/>
							<Checkbox
								id="revealsUnlimited"
								name="revealsUnlimited"
								label={t('unlimited')}
								checked={values.revealsUnlimited}
								onSetValue={setFieldValue}
								disabled={isSubmitting}
								touched={touched.revealsUnlimited}
							/>
						</Flex>
					)}

					<Flex alignItems="flex-end" flexDirection="column" p={3}>
						<TextInput
							type="number"
							name="priceWithoutTax"
							label={t('package_price')}
							placeholder={t('package_price_placeholder')}
							iconRight={
								<Box width={30} textAlign="right">
									{t('czk')}
								</Box>
							}
							css={css`
								text-align: right;
							`}
						/>

						<Text p={0} m={0} fontSize="sm" fontStyle={'italic'}>
							({t('without_vat')})
						</Text>
					</Flex>
					<Flex alignItems="center" justifyContent="space-between" p={3}>
						<Box width={4 / 5}>
							<Button
								variant="outlined"
								onClick={closeModal}
								disabled={isSubmitting}
								px={0}
							>
								{t('cancel')}
							</Button>
						</Box>
						<Box>
							<Button type="submit" variant="primary" disabled={isSubmitting}>
								<Text as="span">{t('save')}</Text>
							</Button>
						</Box>
					</Flex>
				</Paper>
			</Form>
		</Box>
	);
};

export default ProductEdit;
